import {
    AfterViewInit, Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild
} from '@angular/core';
import { CHART_COLOR, PERIOD_CONSTANT } from '@core/constants';
import { CommonHelper, FullscreenService } from '@core/services/common';
import { debounce } from 'lodash';
import { Subscription } from 'rxjs';
@Component({
    selector: 'caremoni-dual-bar-chart',
    templateUrl: './dual-bar-chart.component.html',
    styleUrl: './dual-bar-chart.component.scss'
})
export class DualBarChartComponent implements OnChanges, AfterViewInit, OnInit, OnDestroy {
    @ViewChild('leftBarChartCanvas', { static: false }) leftBarChartCanvas!: ElementRef<HTMLCanvasElement>;
    @ViewChild('rightBarChartCanvas', { static: false }) rightBarChartCanvas!: ElementRef<HTMLCanvasElement>;

    @Input() data: {
        meal: { date: Date; main: number, sub: number; attention_flg: boolean,}[],
        moisture: { date: Date; noteid: string, volume: number }[],
        enteral_feeding: { date: Date, isfeeding: boolean }[],
        leftChart: {value: number, color: string, attention_flg: boolean, date: Date, type: number}[],
        rightChart: {value: number, color: string, attention_flg: boolean, date: Date, type: number}[] 
    } = { meal: [], moisture: [], enteral_feeding: [], leftChart: [], rightChart: [] };
    @Input() config: any;

    fixColNumbersLeft = 6;
    fixColNumbersRight = 6;

    leftLabelTop: string = '';
    rightLabelTop: string = '';
    titleLeftWidth: string = '100%';
    titleRightWidth: string = '100%';
    moistureLeftChart: any = [];
    moistureRightChart: any = [];
    textColor: string = "#fff";

    tubeIndexYesterday: number[] = [];
    tubeIndexToday: number[] = [];
    tubeFeedingTimestampsYesterday = new Map<number, number>(); // periodIndex -> latest timestamp
    tubeFeedingTimestampsToday = new Map<number, number>(); // periodIndex -> latest timestamp

    isDarkMode: boolean = false;
    isFullScreen: boolean = false;

    private debouncedDrawChart = debounce(() => this.drawChart(), 200);
    private observer!: MutationObserver;
    private subscription = new Subscription();

    constructor(private fullScreenService: FullscreenService) {
        this.darkModeListener();
        this.fullscreenListener();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.debouncedDrawChart();
    }

    ngOnInit() {
    }

    ngOnDestroy(): void {
        if (this.observer) this.observer.disconnect();
        if (this.subscription) this.subscription.unsubscribe();
    }

    ngAfterViewInit() {
        this.darkModeChecking();
        this.debouncedDrawChart();
    }

    @HostListener('window:resize')
    onResize() {
        this.debouncedDrawChart();
    }

    drawChart() {
        this.prepareChartData();
        this.renderCharts();
    }

    hasLeftChartData = (): boolean => this.data.leftChart?.some(item => item.value > 0);
    hasRightChartData =(): boolean => this.data.rightChart?.some(item => item.value > 0);

    /**
     * Prepare all chart data before rendering
     */
    private prepareChartData() {
        this.prepareMealData();
        this.prepareEnteralFeedingData();
        this.compareMealAndTubeFeedingTimestamps();
        this.prepareMoistureData();
    }

    /**
     * Classify meal data into left chart (yesterday) and right chart (today)
     * Group by 3 time ranges: 0-11h, 11-16h, 16-24h
     */
    private prepareMealData() {
        this.data.leftChart = [];
        this.data.rightChart = [];
        let leftChart: any = [];
        let rightChart: any = [];

        this.data.meal = CommonHelper.filterPeriodTodayAndYesterday(this.data.meal);

        // Classify meal data by date
        this.data.meal.forEach((x: any) => {
            const input = new Date(x.date);
            const today = new Date();
            input.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);
            const yesterday = new Date(today);
            yesterday.setDate(today.getDate() - 1);

            if(input.getTime() == yesterday.getTime()){
                leftChart.push({value: x.main, color: CHART_COLOR.MEAL_MAIN_YESTERDAY, attention_flg: x.attention_flg, date: x.date, type: 1})
                leftChart.push({value: x.sub, color: CHART_COLOR.MEAL_SUB_YESTERDAY, attention_flg: x.attention_flg, date: x.date,  type: 0})
            } else if(input.getTime() == today.getTime()){
                rightChart.push({value: x.main, color: CHART_COLOR.MEAL_MAIN_TODAY, attention_flg: x.attention_flg, date: x.date, type: 1})
                rightChart.push({value: x.sub, color: CHART_COLOR.MEAL_SUB_TODAY, attention_flg: x.attention_flg, date: x.date, type: 0})
            }
        });

        // Group by time ranges for yesterday (left chart)
        let yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1)
        this.data.leftChart.push(this.PushItemToChart(leftChart, 0, 11, yesterday, CHART_COLOR.MEAL_MAIN_YESTERDAY, 1));
        this.data.leftChart.push(this.PushItemToChart(leftChart, 0, 11, yesterday, CHART_COLOR.MEAL_SUB_YESTERDAY, 0));
        this.data.leftChart.push(this.PushItemToChart(leftChart, 11, 16, yesterday, CHART_COLOR.MEAL_MAIN_YESTERDAY, 1));
        this.data.leftChart.push(this.PushItemToChart(leftChart, 11, 16, yesterday, CHART_COLOR.MEAL_SUB_YESTERDAY, 0));
        this.data.leftChart.push(this.PushItemToChart(leftChart, 16, 24, yesterday, CHART_COLOR.MEAL_MAIN_YESTERDAY, 1));
        this.data.leftChart.push(this.PushItemToChart(leftChart, 16, 24, yesterday, CHART_COLOR.MEAL_SUB_YESTERDAY, 0));

        // Group by time ranges for today (right chart)
        this.data.rightChart.push(this.PushItemToChart(rightChart, 0, 11, new Date(), CHART_COLOR.MEAL_MAIN_TODAY, 1));
        this.data.rightChart.push(this.PushItemToChart(rightChart, 0, 11, new Date(), CHART_COLOR.MEAL_SUB_TODAY, 0));
        this.data.rightChart.push(this.PushItemToChart(rightChart, 11, 16, new Date(), CHART_COLOR.MEAL_MAIN_TODAY, 1));
        this.data.rightChart.push(this.PushItemToChart(rightChart, 11, 16, new Date(), CHART_COLOR.MEAL_SUB_TODAY, 0));
        this.data.rightChart.push(this.PushItemToChart(rightChart, 16, 24, new Date(), CHART_COLOR.MEAL_MAIN_TODAY, 1));
        this.data.rightChart.push(this.PushItemToChart(rightChart, 16, 24, new Date(), CHART_COLOR.MEAL_SUB_TODAY, 0));
    }


    /**
     * Check for enteral feeding (tube feeding) and track latest timestamps for each meal period
     * Store period indexes: 0 = morning (0-11h), 1 = afternoon (11-16h), 2 = evening (16-24h)
     */
    private prepareEnteralFeedingData() {
        this.tubeIndexYesterday = [];
        this.tubeIndexToday = [];
        this.tubeFeedingTimestampsYesterday.clear();
        this.tubeFeedingTimestampsToday.clear();

        this.data.enteral_feeding.forEach((x: any) => {
            const input = new Date(x.date);
            const today = new Date();
            input.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);
            const yesterday = new Date(today);
            yesterday.setDate(today.getDate() - 1);

            if (!x.isfeeding) return;

            // Get hour from the feeding date
            const feedingHour = new Date(x.date).getHours();
            const feedingMinute = new Date(x.date).getMinutes();
            const feedingTime = `${feedingHour.toString().padStart(2, '0')}:${feedingMinute.toString().padStart(2, '0')}`;

            // Determine which period (morning=0, afternoon=1, evening=2)
            let periodIndex = -1;
            if (feedingTime >= PERIOD_CONSTANT.MORNING.START && feedingTime <= PERIOD_CONSTANT.MORNING.END) {
                periodIndex = 0; // Morning
            } else if (feedingTime >= PERIOD_CONSTANT.AFTERNOON.START && feedingTime <= PERIOD_CONSTANT.AFTERNOON.END) {
                periodIndex = 1; // Afternoon
            } else if (feedingTime >= PERIOD_CONSTANT.EVENING.START && feedingTime <= PERIOD_CONSTANT.EVENING.END) {
                periodIndex = 2; // Evening
            }

            // Skip if ALL_DAY or invalid period
            if (periodIndex === -1) return;

            const timestamp = new Date(x.date).getTime();

            // Track latest timestamp for each period
            if (input.getTime() === yesterday.getTime()) {
                const currentMax = this.tubeFeedingTimestampsYesterday.get(periodIndex) || 0;
                this.tubeFeedingTimestampsYesterday.set(periodIndex, Math.max(currentMax, timestamp));
            } else if (input.getTime() === today.getTime()) {
                const currentMax = this.tubeFeedingTimestampsToday.get(periodIndex) || 0;
                this.tubeFeedingTimestampsToday.set(periodIndex, Math.max(currentMax, timestamp));
            }
        });
    }

    /**
     * Compare meal and tube feeding timestamps to determine which should be displayed
     * Only mark tube feeding if tube feeding record is newer than meal record
     */
    private compareMealAndTubeFeedingTimestamps() {
        this.tubeIndexYesterday = [];
        this.tubeIndexToday = [];

        // Helper function to convert time string (HH:mm) to hour number
        const timeToHour = (timeStr: string): number => {
            return parseInt(timeStr.split(':')[0], 10);
        };

        // Define period hour ranges using PERIOD_CONSTANT
        const periods = [
            {
                start: timeToHour(PERIOD_CONSTANT.MORNING.START),
                end: timeToHour(PERIOD_CONSTANT.MORNING.END)
            },
            {
                start: timeToHour(PERIOD_CONSTANT.AFTERNOON.START),
                end: timeToHour(PERIOD_CONSTANT.AFTERNOON.END)
            },
            {
                start: timeToHour(PERIOD_CONSTANT.EVENING.START),
                end: timeToHour(PERIOD_CONSTANT.EVENING.END)
            }
        ];

        // Compare timestamps for yesterday
        periods.forEach((period, periodIndex) => {
            const latestTubeFeedingTimestamp = this.tubeFeedingTimestampsYesterday.get(periodIndex) || 0;

            // Find latest meal timestamp for this period
            let latestMealTimestamp = 0;
            this.data.leftChart.forEach(item => {
                if (item.value > 0 && item.date) {
                    const itemHour = new Date(item.date).getHours();
                    if (itemHour >= period.start && itemHour <= period.end) {
                        const timestamp = new Date(item.date).getTime();
                        latestMealTimestamp = Math.max(latestMealTimestamp, timestamp);
                    }
                }
            });

            // Only mark as tube feeding if tube feeding is newer than meal
            if (latestTubeFeedingTimestamp > 0 && latestTubeFeedingTimestamp > latestMealTimestamp) {
                this.tubeIndexYesterday.push(periodIndex);
            }
        });

        // Compare timestamps for today
        periods.forEach((period, periodIndex) => {
            const latestTubeFeedingTimestamp = this.tubeFeedingTimestampsToday.get(periodIndex) || 0;

            // Find latest meal timestamp for this period
            let latestMealTimestamp = 0;
            this.data.rightChart.forEach(item => {
                if (item.value > 0 && item.date) {
                    const itemHour = new Date(item.date).getHours();
                    if (itemHour >= period.start && itemHour <= period.end) {
                        const timestamp = new Date(item.date).getTime();
                        latestMealTimestamp = Math.max(latestMealTimestamp, timestamp);
                    }
                }
            });

            // Only mark as tube feeding if tube feeding is newer than meal
            if (latestTubeFeedingTimestamp > 0 && latestTubeFeedingTimestamp > latestMealTimestamp) {
                this.tubeIndexToday.push(periodIndex);
            }
        });
    }

    /**
     * Calculate total moisture volume for yesterday and today
     */
    private prepareMoistureData() {
        this.moistureLeftChart = [];
        this.moistureRightChart = [];

        this.data.moisture.forEach((x: any) => {
            const input = new Date(x.date);
            const today = new Date();
            input.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);
            const yesterday = new Date(today);
            yesterday.setDate(today.getDate() - 1);

            if(input.getTime() == yesterday.getTime())
                this.moistureLeftChart.push({value: x.volume, color: CHART_COLOR.MOISTURE_YESTERDAY})
            else if(input.getTime() == today.getTime())
                this.moistureRightChart.push({value: x.volume, color: CHART_COLOR.MOISTURE_TODAY})
        });
    }

    /**
     * Render both left and right charts
     */
    private renderCharts() {
        this.renderBarChart(
            this.leftBarChartCanvas,
            this.data.leftChart,
            this.moistureLeftChart,
            'left',
            this.fixColNumbersLeft
        );

        this.renderBarChart(
            this.rightBarChartCanvas,
            this.data.rightChart,
            this.moistureRightChart,
            'right',
            this.fixColNumbersRight
        );
    }

    /**
     * Generic method to render a bar chart
     * @param canvas - Canvas element reference
     * @param chartData - Chart data array
     * @param moistureData - Moisture data array
     * @param side - 'left' or 'right' to determine which properties to update
     * @param fixColNumbers - Fixed number of columns
     */
    private renderBarChart(
        canvas: ElementRef<HTMLCanvasElement>,
        chartData: any[],
        moistureData: any[],
        side: 'left' | 'right',
        fixColNumbers: number
    ) {
        if (!canvas) return;
        if (!chartData?.length) return;

        const canvasEl = canvas.nativeElement;
        const ctx = canvasEl.getContext('2d');
        if (!ctx) return;

        // Sync canvas internal resolution with CSS rendered size to avoid aspect ratio distortion
        const rect = canvasEl.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        canvasEl.width = rect.width * dpr;
        canvasEl.height = rect.height * dpr;
        ctx.scale(dpr, dpr);

        const chartWidth = rect.width;
        const chartHeight = rect.height;
        const barWidth = chartWidth / (fixColNumbers || chartData.length);

        // Get tube feeding indexes based on side
        const tubeIndexes = side === 'left' ? this.tubeIndexYesterday : this.tubeIndexToday;

        // Calculate moisture sum
        let maxValue = Math.max(...chartData.map(e => e.value)) || 1;
        const sum = moistureData.map((e: any) => e.value).reduce((acc: any, val: any) => Number(acc) + Number(val), 0);

        // Update label and title width based on side
        if (side === 'left') {
            this.leftLabelTop = `${sum}`;
        } else {
            this.rightLabelTop = `${sum}`;
        }

        ctx.clearRect(0, 0, chartWidth, chartHeight);

        // Check if we have data to display
        if(maxValue == 1 && sum == 0){
            if (side === 'left') {
                this.leftLabelTop = '';
                this.titleLeftWidth = '0%';
            } else {
                this.rightLabelTop = '';
                this.titleRightWidth = '0%';
            }
        } else {
            // Fixed max value for consistent scaling
            maxValue = 10;
            const titleWidth = (100/6) * chartData?.length + '%';

            if (side === 'left') {
                this.titleLeftWidth = titleWidth;
            } else {
                this.titleRightWidth = titleWidth;
            }
        }

        // Draw bars (skip bars for tube feeding groups)
        // Each group has 2 bars (main + sub), so groupIndex = Math.floor(index / 2)
        const groupColNumber = 2; // main + sub per period
        chartData.forEach((col, index) => {
            const groupIndex = Math.floor(index / groupColNumber);
            const isTubeFeeding = tubeIndexes.includes(groupIndex);

            if (isTubeFeeding) return; // Skip bars for tube feeding groups

            const barHeight = ((col.value == 0 ? 0 : col.value) / maxValue) * chartHeight;
            const x = index * barWidth;
            const y = chartHeight - barHeight;

            ctx.fillStyle = col.color;
            ctx.fillRect(x + 2, y, barWidth - 2, barHeight);
        });

        // Total 3 groups: morning (0), afternoon (1), evening (2)
        const totalGroups = 3;

        // Draw dashed lines only if chart has data (skip lines for tube feeding groups)
        const hasData = chartData.some(col => col.value > 0);
        if (hasData) {
            ctx.strokeStyle = this.textColor;
            ctx.setLineDash([5, 5]);
            ctx.lineWidth = 0.5;

            const midY = chartHeight * 0.5;
            const topY = 4 / (window.devicePixelRatio || 1);

            for (let groupIndex = 0; groupIndex < totalGroups; groupIndex++) {
                const isTubeFeeding = tubeIndexes.includes(groupIndex);
                if (isTubeFeeding) continue; // Skip lines for tube feeding groups

                const startColIndex = groupIndex * groupColNumber;
                const endColIndex = (groupIndex + 1) * groupColNumber;

                const startX = startColIndex * barWidth;
                const endX = endColIndex * barWidth;

                // Mid line
                ctx.beginPath();
                ctx.moveTo(startX, midY);
                ctx.lineTo(endX, midY);
                ctx.stroke();

                // Top line
                ctx.beginPath();
                ctx.moveTo(startX, topY);
                ctx.lineTo(endX, topY);
                ctx.stroke();
            }

            ctx.setLineDash([]);
        }

    }

    readonly groupIndexes = [0, 1, 2]; // 3 periods: morning, afternoon, evening

    darkModeListener() {
        this.observer = new MutationObserver(() => this.darkModeChecking());
        this.observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }

    darkModeChecking() {
        const bodyClass = document.body.className;
        this.isDarkMode = bodyClass.includes("dark");
        this.textColor = this.isDarkMode ? "#fff" : "#000";
        this.debouncedDrawChart();
    }

    fullscreenListener() {
        this.subscription.add(
            this.fullScreenService.fullscreenState$.subscribe(state => {
                this.isFullScreen = state.isFullscreen;
                // Use debounced draw to avoid multiple redraws when fullscreen toggles
                // (fullscreen toggle also triggers resize event)
                this.debouncedDrawChart();
            })
        );
    }

    PushItemToChart(chart: any = [], startHours: number, endHours: number, date: Date, color: string, type: number){
        let arr: any = [];
        chart.forEach((x: any) =>{
            let xDate = new Date(x.date);
            const hour = new Date(x.date).getHours();
            if(xDate.getFullYear() === date.getFullYear() && xDate.getMonth() == date.getMonth() && xDate.getDate() == date.getDate() 
                && hour >= startHours && hour < endHours && x.type == type) arr.push(x);
        })
        if(arr.length == 0) return {value: 0, color: color, attention_flg: false, date: date, type: type}
        else{
            let max = new Date(arr[0].date);
            let value = arr[0];
            arr.forEach((x: any) =>{
                if(new Date(x.date) > max){
                    max = new Date(x.date); 
                    value = {value: x.value, color: x.color, attention_flg: x.attention_flg, date: x.date, type: x.type}
                }
            });
            return value;
        }
    }
}
